import type { FeedItem, FeedTypeCategory } from "@compass/shared/schema/api/feed";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "components/elements";
import { ToggleGroup, ToggleGroupItem } from "components/shad-components/ui/toggle-group";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { ClientPost, PostCreate } from "@compass/shared/types";
import { EditPostTrigger, SocialPostWithComments } from "./SocialMain";
import { SocialPostEditor } from "./SocialPostEditor";
import { FeedBlogPostCard } from "./cards/FeedBlogPostCard";
import { FeedCommentsCard } from "./cards/FeedCommentsCard";
import { FeedCompassEventCard } from "./cards/FeedCompassEventCard";
import { FeedEventCard } from "./cards/FeedEventCard";
import { FeedSummaryCard } from "./cards/FeedSummaryCard";
import { FeedVendorCard } from "./cards/FeedVendorCard";
import { FeedWebinarCard } from "./cards/FeedWebinarCard";
import { PostModal } from "./post/PostModal";

function getDeviceTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}

function getFeedItemKey(item: FeedItem): string {
    switch (item.type) {
        case "newPost":
            return `newPost-${(item.post as { id: number }).id}`;
        case "newBlogPost":
            return `newBlogPost-${item.blogPost.id}`;
        case "upcomingEvent":
            return `upcomingEvent-${item.event.id}`;
        case "upcomingCompassEvent":
            return `upcomingCompassEvent-${item.compassEvent.id}`;
        case "vendorApproved":
            return `vendorApproved-${item.vendor.id}`;
        case "newDigest":
            return `newDigest-${item.summary.id}`;
        case "upcomingWebinar":
            return `upcomingWebinar-${item.webinar.id}`;
        case "postCommentsAdded":
            return `postComments-${item.postId}-${item.sortDate}`;
        case "blogPostCommentsAdded":
            return `blogPostComments-${item.blogPostId}-${item.sortDate}`;
        case "vendorCommentsAdded":
            return `vendorComments-${item.vendorId}-${item.sortDate}`;
    }
}

const FeedItemCard = ({
    item,
    setActivePost,
}: {
    item: FeedItem;
    setActivePost: (post: ClientPost) => void;
}) => {
    switch (item.type) {
        case "newPost":
            return (
                <div className="group relative rounded-2xl border border-[#d4d5d5] p-4 shadow-none">
                    <SocialPostWithComments
                        post={item.post as ClientPost}
                        limit={2}
                        setActivePost={setActivePost}
                    />
                </div>
            );
        case "newBlogPost":
            return <FeedBlogPostCard {...item} />;
        case "upcomingEvent":
            return <FeedEventCard {...item} />;
        case "upcomingCompassEvent":
            return <FeedCompassEventCard {...item} />;
        case "vendorApproved":
            return <FeedVendorCard {...item} />;
        case "newDigest":
            return <FeedSummaryCard {...item} />;
        case "upcomingWebinar":
            return <FeedWebinarCard {...item} />;
        case "postCommentsAdded":
        case "blogPostCommentsAdded":
        case "vendorCommentsAdded":
            return <FeedCommentsCard {...item} />;
        default:
            return null;
    }
};

const FEED_FILTER_OPTIONS: { value: FeedTypeCategory; label: string }[] = [
    { value: "posts", label: "Posts" },
    { value: "articles", label: "Articles" },
    { value: "events", label: "Events" },
    { value: "vendors", label: "Vendors" },
    { value: "digests", label: "Digests" },
    { value: "webinars", label: "Webinars" },
];

export const UnifiedFeed = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = trpc.useContext();
    const [activePost, setActivePost] = useState<ClientPost | null>(null);
    const currentUser = useCurrentUser();
    const [isPostEditOpen, setIsPostEditOpen] = useState<"new-post" | undefined>();
    const [selectedTypes, setSelectedTypes] = useState<FeedTypeCategory[]>(
        FEED_FILTER_OPTIONS.filter((o) => o.value !== "events" && o.value !== "digests").map(
            (o) => o.value,
        ),
    );

    const { data, isFetching, hasNextPage, isInitialLoading, fetchNextPage } =
        trpc.feed.unified.useInfiniteQuery(
            {
                limit: 10,
                types: selectedTypes.length > 0 ? selectedTypes : undefined,
                timezone: getDeviceTimezone(),
            },
            {
                placeholderData: (prev) => prev,
                getNextPageParam: (lastPage) => lastPage.nextCursor,
            },
        );

    const footerRef = useRef(null);

    useEffect(() => {
        if (!footerRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            });
        });

        observer.observe(footerRef.current);

        return () => {
            if (!footerRef.current) return;
            observer.unobserve(footerRef.current);
        };
    }, [footerRef, hasNextPage, fetchNextPage]);

    const createPostMutation = trpc.social.posts.create.useMutation();

    const handleCreatePost = (params: PostCreate, callback: () => void) => {
        if (createPostMutation.isPending) return;

        createPostMutation.mutate(params, {
            onSuccess: () => {
                queryClient.feed.unified.invalidate();
                queryClient.social.posts.latest.invalidate();
                enqueueSnackbar("Post created successfully.", { variant: "success" });
                callback();
            },
            onError: (e) => {
                const zodMessage =
                    (e?.data?.zodError?.flat as any)?.properties?.content?.errors?.[0] ??
                    e?.data?.zodError?.flat?.errors?.[0];

                if (zodMessage) {
                    enqueueSnackbar(zodMessage, { variant: "error" });
                } else {
                    enqueueSnackbar(e.message || "Failed to create post", { variant: "error" });
                }
            },
        });
    };

    const allItems = useMemo<FeedItem[]>(() => {
        if (!data?.pages || data.pages.length === 0) return [];
        return data.pages.flatMap((page) => page.items) as unknown as FeedItem[];
    }, [data]);

    if (isInitialLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner className="text-slate-500" />
            </div>
        );
    }

    return (
        <>
            <SocialPostEditor
                isLoading={createPostMutation.isPending}
                handleEditPost={(params: PostCreate, callback: () => void) =>
                    handleCreatePost(params, callback)
                }
                isOpen={isPostEditOpen}
                setIsOpen={() => setIsPostEditOpen(isPostEditOpen ? undefined : "new-post")}
                currentUser={currentUser}
            >
                <div>
                    <EditPostTrigger />
                </div>
            </SocialPostEditor>

            <div className="mb-4 flex flex-wrap gap-2">
                <ToggleGroup
                    type="multiple"
                    value={selectedTypes}
                    onValueChange={(value) => setSelectedTypes(value as FeedTypeCategory[])}
                >
                    {FEED_FILTER_OPTIONS.map((opt) => (
                        <ToggleGroupItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </ToggleGroupItem>
                    ))}
                </ToggleGroup>
            </div>

            <div className="flex flex-col gap-4">
                {allItems.map((item) => (
                    <FeedItemCard
                        key={getFeedItemKey(item)}
                        item={item}
                        setActivePost={setActivePost}
                    />
                ))}
            </div>

            {activePost && (
                <PostModal
                    isOpen={Boolean(activePost)}
                    onClose={() => setActivePost(null)}
                    post={activePost}
                />
            )}

            {hasNextPage && (
                <div ref={footerRef} className="flex items-center justify-center pt-8">
                    {isFetching && <Spinner className="h-7 w-7 text-slate-500" />}
                </div>
            )}

            {!hasNextPage && allItems.length > 0 && (
                <p className="py-8 text-center text-sm text-gray-400">You're all caught up!</p>
            )}

            {!isInitialLoading && allItems.length === 0 && (
                <div className="py-16 text-center">
                    <p className="text-lg text-gray-500">Nothing in the feed yet</p>
                    <p className="mt-1 text-sm text-gray-400">
                        Posts, articles, events, and more will appear here.
                    </p>
                </div>
            )}
        </>
    );
};
